/**
 * priceMode.ts — convert a user-entered selling price between "With GST"
 * (tax-inclusive) and "Without GST" (tax-exclusive) entry modes, and into the
 * store's canonical storage convention.
 *
 * Why this exists: when adding a product the shopkeeper may type the price the
 * customer pays (inclusive of GST) or the pre-tax price. We normalise it to the
 * store's billing convention (the global TAX_INCLUSIVE setting) at save time, so
 * the billing engine and every report keep working unchanged — no per-product
 * flag is stored, which also keeps it clear of the sync layer.
 *
 * All money is in rupees; results are rounded to 2 decimals.
 */

function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

/** 1 + total tax fraction, e.g. 18% GST + 0% cess → 1.18. */
export function grossFactor(gstRate: number, cessRate = 0): number {
  const f = 1 + (gstRate + cessRate) / 100;
  return f > 0 ? f : 1;
}

/**
 * Convert a price from one entry mode to the other (inclusive ↔ exclusive).
 * Store-agnostic — used to keep the form field's number meaningful when the
 * user flips the "includes GST" checkbox.
 */
export function convertEntryMode(
  price: number,
  fromIncludesGst: boolean,
  toIncludesGst: boolean,
  gstRate: number,
  cessRate = 0,
): number {
  if (fromIncludesGst === toIncludesGst) return round2(price);
  const f = grossFactor(gstRate, cessRate);
  return round2(toIncludesGst ? price * f : price / f);
}

/**
 * Normalise an entered price into the store's canonical stored convention.
 *
 * @param entered            the number the user typed
 * @param enteredIncludesGst true if that number is tax-inclusive ("With GST")
 * @param storeIsInclusive   the store's TAX_INCLUSIVE billing convention
 */
export function toStoredSellingPrice(
  entered: number,
  gstRate: number,
  cessRate: number,
  enteredIncludesGst: boolean,
  storeIsInclusive: boolean,
): number {
  if (enteredIncludesGst === storeIsInclusive) return round2(entered);
  const f = grossFactor(gstRate, cessRate);
  // Entered inclusive but store stores exclusive → strip tax; opposite → add tax.
  return round2(enteredIncludesGst ? entered / f : entered * f);
}

/**
 * Inverse of {@link toStoredSellingPrice}: given a stored price, produce the
 * number to show in the form for a chosen entry mode (used when editing).
 */
export function toEnteredSellingPrice(
  stored: number,
  gstRate: number,
  cessRate: number,
  wantIncludesGst: boolean,
  storeIsInclusive: boolean,
): number {
  if (wantIncludesGst === storeIsInclusive) return round2(stored);
  const f = grossFactor(gstRate, cessRate);
  // Stored exclusive but want inclusive → add tax; stored inclusive but want
  // exclusive → strip tax.
  return round2(wantIncludesGst ? stored * f : stored / f);
}

/** Split an entered price into its pre-tax, tax, and inclusive components. */
export function priceBreakdown(
  entered: number,
  gstRate: number,
  cessRate: number,
  enteredIncludesGst: boolean,
): { preTax: number; tax: number; inclusive: number } {
  const f = grossFactor(gstRate, cessRate);
  const preTax = enteredIncludesGst ? entered / f : entered;
  const inclusive = enteredIncludesGst ? entered : entered * f;
  return { preTax: round2(preTax), tax: round2(inclusive - preTax), inclusive: round2(inclusive) };
}

/**
 * The selling price to SHOW for a product, in the terms the shopkeeper thinks in.
 *
 * `product.sellingPrice` is stored in the store's canonical convention
 * (TAX_INCLUSIVE), which is not necessarily the convention the price was typed
 * in. A shop that stores exclusive but enters "With GST" would type 1450 for an
 * 18% item and have 1228.81 written to the row — and the inventory list showed
 * that 1228.81 back, a number the shopkeeper had never seen and could not match
 * to their own shelf label.
 *
 * ProductForm already inverts this when it opens a product for editing. This is
 * the same inversion for read-only surfaces, so the list, the detail panel and
 * the edit form all agree on what "the price" is.
 */
export function displaySellingPrice(
  stored: number,
  gstRate: number,
  cessRate: number,
  entryModeIncludesGst: boolean,
  storeIsInclusive: boolean,
): number {
  return toEnteredSellingPrice(stored, gstRate, cessRate, entryModeIncludesGst, storeIsInclusive);
}
